// ============================================================
// ConsigneGamme — « Comment usineriez-vous cette pièce ? »
// ------------------------------------------------------------
// C'est le champ par lequel le deviseur DÉCRIT sa façon de faire, en
// français : « je pars d'un étiré 40×10, prise 1 en étau sur le petit
// côté ». L'agent révisera la gamme existante à partir de là — il n'en
// produira pas une nouvelle, et l'écran montrera le diff opération par
// opération (§8.6).
//
// ── Pourquoi il est visible AVANT que la révision existe ──
//
// La révision de gamme est le lot F2 ; elle n'est pas là. Le champ, si.
// **Un champ visible et inerte vaut mieux qu'une promesse invisible** :
// il dit ce que le programme va savoir faire, il se remplit dès
// aujourd'hui, et la consigne est CONSERVÉE en base (`consigne_gamme`)
// — donc la première pièce chiffrée après F2 profitera de ce qui a été
// écrit avant.
//
// Il est marqué sans ambiguïté « pas encore actif ». Un champ qui a
// l'air de marcher et ne fait rien est un mensonge d'interface.
//
// ── Ce que ce champ doit devenir, et ce qu'il ne doit PAS rester ──
//
// C'est un FILET DE DÉPART, pas la cible. Le jour où l'agent aura appris
// la façon d'usiner de Mertens, il n'aura plus besoin qu'on la lui dise
// — et l'écran Apprentissage suit précisément ce nombre : la part des
// pièces chiffrées SANS consigne doit monter (CLAUDE.md §2bis).
// ============================================================

import { Textarea } from '../../ui/TextField.jsx';
import Button from '../../ui/Button.jsx';
import { machinesNommees } from '@shared/consigneMachines.js';

export function ConsigneGamme({
  piece, disabled, onChange, aUneGamme = false,
  onChiffrer = null, chiffrage = false, machines = [],
}) {
  const consigne = String(piece.consigne_gamme ?? '').trim();
  // Une machine nommée absente du parc se DIT ici, en permanence — pas
  // seulement dans le toast de l'estimation (27 octobre 2026, 2026-10-036).
  const inconnues = machines.length ? machinesNommees(consigne, machines).inconnues : [];

  return (
    <div className="consigne">
      <div className="row row--between">
        <p className="caps">Comment usineriez-vous cette pièce ?</p>
        {/* ══════════════════════════════════════════════════════════
            REPARTIR DE ZÉRO — réunion du 1er septembre 2026.
            ──────────────────────────────────────────────────────────
            Charles : « pas de moyen de repartir de zéro sur les
            consignes d'une pièce ».

            Techniquement il pouvait vider le champ à la main. Ce qui
            manquait n'est pas le geste, c'est de savoir qu'il EXISTE et
            ce qu'il emporte : la phrase repart au modèle à chaque
            chiffrage, donc l'effacer n'est pas cosmétique, c'est une
            décision sur ce que l'agent lira la prochaine fois.

            Le cas qui l'a rendu nécessaire : sur le devis 2026-09-005,
            la consigne était devenue « Il faut vraiment beaucoup plus
            de temps pour faire la pièce » — un message de révision qui
            avait remplacé la description d'usinage (corrigé côté
            serveur le 7 septembre). Sans ce bouton, on relit une phrase
            qu'on n'a pas écrite là et on ne sait pas qu'on peut la
            retirer.

            Il vide le champ. Depuis le 6 octobre 2026 le devis
            s'enregistre seul 2,5 s après la dernière modification : le
            filet « ne pas enregistrer annule tout » n'existe plus. */}
        {consigne.length ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={disabled}
            title="Vider la consigne de cette pièce"
            onClick={() => onChange({ consigne_gamme: '' })}
          >
            Repartir de zéro
          </Button>
        ) : null}
      </div>
      <Textarea
        label="Comment usineriez-vous cette pièce ?"
        hideLabel
        // ── UN ENCART À LA TAILLE DE LA CONSIGNE — 15 septembre 2026 ──
        // Trois lignes, pour des consignes qui en font dix (« OP 1 :
        // serrage… », une ligne par prise) : Charles écrivait à
        // l'aveugle dans une fente. Cinq lignes au moins sur toute la largeur, et le champ
        // grandit avec le texte au lieu de le faire défiler.
        rows={Math.max(5, (piece.consigne_gamme ?? '').split('\n').length + 1)}
        value={piece.consigne_gamme ?? ''}
        disabled={disabled}
        placeholder="Je pars d’un étiré 40×10, prise 1 en étau sur le petit côté, reprise en mors doux pour l’alésage…"
        hint={
          aUneGamme
            ? 'Elle est conservée avec la pièce et repart à chaque chiffrage. Pour CORRIGER la gamme ci-dessous, servez-vous du champ qui la suit.'
            : 'Elle part avec le plan au premier chiffrage : c’est elle qui oriente la gamme. Vous pouvez chiffrer sans rien écrire.'
        }
        onChange={(e) => onChange({ consigne_gamme: e.target.value })}
      />
      {inconnues.length ? (
        <p className="field__hint" role="alert">
          <strong>{inconnues.map((n) => `« ${n} »`).join(', ')}</strong> : machine inconnue au parc.
          L’agent chiffrera sur le parc réel — vérifiez le nom, ou ajoutez la machine dans les Paramètres.
        </p>
      ) : null}

      {/* ══════════════════════════════════════════════════════════
          CHIFFRER SE DEMANDE, ET ÇA SE DEMANDE ICI — 27 août 2026
          ══════════════════════════════════════════════════════════

          Le dépôt d'un plan lançait les deux agents. La consigne ne
          POUVAIT donc pas être entendue au premier chiffrage : on
          chiffrait avant d'avoir demandé. Mesuré sur l'historique —
          107 estimations automatiques, puis 72 révisions payées
          ensuite, et une pièce estimée 23 fois.

          Le bouton est SOUS le champ, et c'est tout l'objet : on écrit
          comment on ferait, puis on chiffre. Dans cet ordre. */}
      {onChiffrer && !aUneGamme ? (
        <div className="row row--between">
          <Button
            size="sm"
            variant="primary"
            disabled={disabled || chiffrage}
            onClick={onChiffrer}
          >
            {chiffrage ? 'Chiffrage en cours…' : 'Chiffrer la pièce'}
          </Button>
          <span className="field__hint">
            {consigne.length
              ? 'Votre phrase part avec le plan : elle oriente la gamme dès le premier chiffrage.'
              : 'Vous pouvez chiffrer sans rien écrire — l’agent proposera une gamme de bon sens.'}
          </span>
        </div>
      ) : null}

      {/* ── LA RÉVISION A DÉMÉNAGÉ SOUS LA GAMME — 27 août 2026 ──
          Ce champ portait deux gestes de nature opposée : « comment
          usineriez-vous cette pièce ? », qui ORIENTE, et « prends le NLX
          sur l'OP 20 », qui CORRIGE. Le second suppose qu'on voie la
          gamme ; il vit maintenant dessous, dans `RevisionGamme`. */}
    </div>
  );
}

export default ConsigneGamme;
